import { action, computed, observable } from 'mobx';
import MessageCenter from '../MessageCenter/MessageCenterVM';
import { IElement } from './Screenshot';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { DefinedMapRequest } from 'cd-eft/_codegen_/openapi-generator/eft-set';

export interface ISelection {
    name: string,
    value: number,
    optionDisabled?: boolean
}

export class ScreenshotPreviewVM {
    @observable isOpen: boolean = false;
    @observable isLoading: boolean = false;
    @observable readonly title: string = 'Map Image Preview';
    @observable imageData: string = '';
    private projectId?: string;
    private sessionId?: string;
    private isTerrace: boolean = false;
    private elements?: IElement[];

    @observable imageTypeOptions: ISelection[] = [];
    @observable selectedImageType: number = 0;

    @computed get saveDisabled(): boolean {
        return (this.selectedImageType <= 0);
    }

    private resolveDialogResult?: (value: boolean) => void;

    @action.bound async handleImageTypeChange(event) {
        const { name, value } = event.target;
        this.selectedImageType = value;
    }

    @action.bound showDialog(
        data: string,
        projectId: string,
        sessionId: string,
        isTerrace: boolean,
        options: ISelection[],
        elements?: IElement[]
    ): Promise<boolean> {
        const currentPromise: Promise<boolean> = new Promise((resolve, reject) => {
            this.resolveDialogResult = resolve;
        });
        this.imageData = data;
        this.projectId = projectId;
        this.sessionId = sessionId;
        this.isTerrace = isTerrace;
        this.elements = elements;

        this.imageTypeOptions = options;
        if (options.length === 1) {
            this.selectedImageType = 3;
        } else {
            this.selectedImageType = 0;
        }

        this.isOpen = true;
        return currentPromise;
    }

    @action.bound refocus(elementToFocus) {
        if (elementToFocus && document.body.contains(elementToFocus)) {
            (elementToFocus as HTMLElement).focus();
        }
    }

    @action.bound async accept(): Promise<void> {
        if (this.resolveDialogResult) {
            if (this.projectId) {
                try {
                    this.isLoading = true;
                    let definedMapRequest: DefinedMapRequest = {
                        designElementIds: [-1],
                        designElementTypeId: 0, // Placeholder value
                        imageTypeId: 0,
                        profileImageBase64String: '',
                        profileOrCrossSection: '',
                        stationId: '',
                        structureMapImageBase64String: '',
                        structureProfilesId: 0,
                        twoDimensionalGeometryJson: '',
                    };

                    if (this.isTerrace &&
                        ((Number(this.selectedImageType) === 1) ||
                            (Number(this.selectedImageType) === 2))) {
                        definedMapRequest = {
                            ...definedMapRequest,
                            designElementTypeId: 20,
                            imageTypeId: Number(this.selectedImageType),
                            stationId: this.sessionId,
                            structureMapImageBase64String: this.imageData,
                        };
                    } else {
                        if (this.elements && this.elements.length > 0) {
                            definedMapRequest = {
                                ...definedMapRequest,
                                designElementIds: this.elements.map(e => e.designElementId),
                                designElementTypeId: this.elements[0].designElementTypeId,
                                imageTypeId: Number(this.selectedImageType),
                                structureMapImageBase64String: this.imageData,
                            };
                        } else if (this.elements === undefined) {
                            definedMapRequest = {
                                ...definedMapRequest,
                                designElementTypeId: 24,
                                imageTypeId: Number(this.selectedImageType),
                                stationId: this.projectId, // Assign correct value
                                structureMapImageBase64String: this.imageData,
                            };
                        }
                    }

                    const response = await SETAPI.StructureProfileController.generateUserDefinedMapUsingPOST(definedMapRequest);

                    // console.warn(response);
                    this.isLoading = false;
                    if (response && response.data &&
                        response.data.errorResponse &&
                        response.data.errorResponse.errorCode) {
                        await MessageCenter.addErrorFromCode(response.data.errorResponse.errorCode);
                    }
                } catch (e) {
                    await MessageCenter.addErrorFromCode('Common_20');
                }
            }
            this.isLoading = false;
            //close dialog
            this.isOpen = false;
            //resolve the promise
            this.resolveDialogResult(true);
            //discard callback handle
            this.resolveDialogResult = undefined;
        }
    }

    @action.bound cancel(): void {
        if (this.resolveDialogResult) {
            //close dialog
            this.isOpen = false;
            //resolve the promise
            this.resolveDialogResult(false);
            //discard callback handle
            this.resolveDialogResult = undefined;
        }
    }

    public static async savePlanWithoutDialog(data: string, sessionId: string) {
        const definedMapRequest: DefinedMapRequest = {
            designElementIds: [-1],
            designElementTypeId: 20,
            imageTypeId: 1,
            profileImageBase64String: '',
            profileOrCrossSection: '',
            stationId: sessionId,
            structureMapImageBase64String: data,
            structureProfilesId: 0,
            twoDimensionalGeometryJson: '',
        };

        const response = await SETAPI.StructureProfileController.generateUserDefinedMapUsingPOST(definedMapRequest);

        if (response && response.data &&
            response.data.errorResponse &&
            response.data.errorResponse.errorCode) {
            throw new Error();
        }
    }
}